import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { Toaster } from "react-hot-toast";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AdminRoute } from "./components/AdminRoute";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Invitations } from "./components/Invitations";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PublicRoute } from "./components/PublicRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./contexts/AuthContext";
import { contactPageEnabled } from "./utils/featureFlags";
// import { ProfileCompletionPrompt } from "./components/ProfileCompletionPrompt";
import { AboutPage } from "./pages/About";
import AdminGuidePage from "./pages/AdminGuidePage";
import { AuditorCertificationPage } from "./pages/AuditorCertificationPage";
import { ChecklistConnectedPage } from "./pages/ChecklistConnectedPage";
import { ContactPage } from "./pages/ContactPage";
import { ConnectedAdminImpactEstimateReports } from "./pages/ConnectedAdminImpactEstimateReports";
import { ConnectedAdminLabsPage } from "./pages/ConnectedAdminLabsPage";
import { ConnectedDashboardPage } from "./pages/ConnectedDashboardPage";
import { ConnectedVariablesInputPage } from "./pages/ConnectedVariablesInputPage";
import { EmailActivation } from "./pages/EmailActivation";
import FAQPage from "./pages/FAQ";
import { ForgotPassword } from "./pages/ForgotPassword";
import HomePage from "./pages/Home";
import { LabDetailsPage } from "./pages/LabDetailsPage.tsx";
import { Login } from "./pages/Login";
import { OrganisationRegister } from "./pages/OrganisationRegister";
import PartnersSponsorsPage from "./pages/PartnersSponsorsPage";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { Register } from "./pages/Register";
import { ResendActivation } from "./pages/ResendActivation";
import { ResetPassword } from "./pages/ResetPassword";
import { TermsOfUse } from "./pages/TermsOfUse";
import UserGuidePage from "./pages/UserGuidePage";

const queryClient = new QueryClient();

const L0Routes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={
        <PublicRoute>
          <HomePage />
        </PublicRoute>
      }
      />
      <Route path="/about" element={
        <PublicRoute>
          <AboutPage />
        </PublicRoute>
      }
      />
      <Route path="/faq" element={
        <PublicRoute>
          <FAQPage />
        </PublicRoute>
      }
      />
      <Route path="/partners-sponsors" element={
        <PublicRoute>
          <PartnersSponsorsPage />
        </PublicRoute>
      }
      />
      <Route path="/privacy" element={
        <PublicRoute>
          <PrivacyPolicy />
        </PublicRoute>
      }
      />
      <Route path="/terms" element={
        <PublicRoute>
          <TermsOfUse />
        </PublicRoute>
      }
      />
      {contactPageEnabled && (
        <Route path="/contact" element={
          <PublicRoute>
            <ContactPage />
          </PublicRoute>
        }
        />
      )}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      }
      />
      <Route path="/register" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      }
      />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPassword />
        </PublicRoute>
      }
      />

      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/activate/:token" element={<EmailActivation />} />
      <Route path="/resend-activation" element={<ResendActivation />} />
      <Route path="/register-organisation" element={
        <PublicRoute>
          <OrganisationRegister />
        </PublicRoute>
      }
      />

      {/* Protected Routes */}
      <Route path="/dashboard">
        <Route index element={
          <ProtectedRoute>
            <ConnectedDashboardPage />
          </ProtectedRoute>
        }
        />
        <Route id="labs" path="labs" element={
          <AdminRoute>
            <ConnectedAdminLabsPage />
          </AdminRoute>
        }
        />
        <Route id="lab-dashboard" path="lab-dashboard" element={
          <Navigate to="/dashboard/labs" replace />
        }
        />
        <Route id="variables-input" path="variables" element={
          <AdminRoute>
            <ConnectedVariablesInputPage />
          </AdminRoute>
        }
        />
        <Route id="impact-reports" path="impact-reports" element={
          <AdminRoute>
            {/*<ConnectedImpactReportsPage />*/}
            <ConnectedAdminImpactEstimateReports />
          </AdminRoute>
        }
        />
        {/* Deprecated Routes, redirect to the new lab dashboard */}
        <Route id="register-lab" path="register-lab" element={
          <Navigate to="/dashboard/labs" replace />
        }
        />
        <Route id="view-labs" path="view-labs" element={
          <Navigate to="/dashboard/labs" replace />
        }
        />
      </Route>
      <Route path="/user-guide" element={
        <ProtectedRoute>
          <UserGuidePage />
        </ProtectedRoute>
      }
      />
      <Route path="/admin-guide" element={
        <AdminRoute>
          <AdminGuidePage />
        </AdminRoute>
      }
      />
      <Route path="/invitations" element={
        <ProtectedRoute>
          <Invitations />
        </ProtectedRoute>
      }
      />
      <Route path="/lab/:labId" element={
        <ProtectedRoute>
          <LabDetailsPage />
        </ProtectedRoute>
      }
      />
      <Route path="/certifications/:certificationId/checklist" element={
        <ProtectedRoute>
          <ChecklistConnectedPage />
        </ProtectedRoute>
      }
      />
      <Route path="/certifications/:certificationId/checklists/:instanceId" element={
        <ProtectedRoute>
          <ChecklistConnectedPage />
        </ProtectedRoute>
      }
      />
      <Route path="/auditor/dashboard" element={
        <ProtectedRoute>
          <Navigate to="/dashboard" replace />
        </ProtectedRoute>
      }
      />
      <Route path="/auditor/certification/:certId" element={
        <ProtectedRoute>
          <AuditorCertificationPage />
        </ProtectedRoute>
      }
      />
      {/*<Route path="/auditor-management" element={*/}
      {/*    <ProtectedRoute>*/}
      {/*        <AuditorManagement />*/}
      {/*    </ProtectedRoute>*/}
      {/*} />*/}
      {/*<Route path="/audit/:labId/:level" element={*/}
      {/*    <ProtectedRoute>*/}
      {/*        <AuditorChecklist />*/}
      {/*    </ProtectedRoute>*/}
      {/*} />*/}

      {/* Catch all route - 404 */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <div data-theme="emerald" className="min-h-screen bg-[var(--l0-background-color)] flex flex-col">
            <Header />
            <main className="flex-1 flex flex-col">
              <L0Routes />
            </main>
            <Footer />
          </div>
          <Toaster
            position="top-center"
            containerStyle={{ zIndex: 99999 }}
            toastOptions={{ duration: 5000 }}
          />
          {/* <ProfileCompletionPrompt /> */}
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
